import _ from 'lodash';
import { UploadVideoWriteModel } from './models/uploadVideo.model';

export const getUploadCompletion = <T>(
  items: Array<{ state: T }>,
  filterState: T,
): { count: number; percent: number } => {
  const all = items.length;
  if (all === 0) return { count: 0, percent: 0 };

  const amount = items.filter((i) => i.state === filterState).length;
  return {
    count: amount,
    percent: Math.round((amount / all) * 100),
  };
};

export const floatingWindowText = (
  uploadMap: UploadVideoWriteModel['uploadMap'],
) => {
  const uploadArr = Object.values(uploadMap);
  const all = uploadArr.length;
  if (all === 0)
    return {
      title: '',
    };

  const finished = getUploadCompletion(uploadArr, 'finished');
  const failed = getUploadCompletion(uploadArr, 'error');
  if (finished.count === all) {
    return {
      title: 'Upload finished',
      detailsLeft: `All ${all} ${
        all === 1 ? 'file' : 'files'
      } uploaded successfully`,
    };
  }
  if (finished.count + failed.count === all) {
    return {
      title: 'Upload finished',
      detailsLeft: `${failed.count} out of ${all} ${
        all === 1 ? 'file' : 'files'
      } failed to upload`,
    };
  }

  return {
    title: `Uploading ${all} ${all === 1 ? 'file' : 'files'}`,
    detailsLeft: 'Do not close browser tab!',
    detailsRight: `${finished.percent}% completed...`,
  };
};

export const mappedLists = (
  files: UploadVideoWriteModel['files'],
  uploadMap: UploadVideoWriteModel['uploadMap'],
): {
  files: UploadVideoWriteModel['files'];
  failedFiles: UploadVideoWriteModel['files'];
  invalidFiles: UploadVideoWriteModel['files'];
} => {
  const failedIds = Object.values(uploadMap)
    .filter((i) => i.state === 'error' && i.fileId)
    .map((i) => i.fileId);

  return {
    files: files
      .filter((f) => !f.error)
      .filter((i) => !failedIds.includes(i.id)),
    failedFiles: files.filter((i) => failedIds.includes(i.id)),
    invalidFiles: files.filter((f) => !!f.error),
  };
};

export const getFailedFiles = (
  files: UploadVideoWriteModel['files'],
  uploadMap: UploadVideoWriteModel['uploadMap'],
) => {
  const failedIds = Object.values(uploadMap)
    .filter((i) => i.state === 'error' && i.fileId)
    .map((i) => i.fileId);

  return files.filter((i) => failedIds.includes(i.id));
};

export const getListStatus = (
  isUploading: boolean,
  files: UploadVideoWriteModel['files'],
  uploadMap: UploadVideoWriteModel['uploadMap'],
) => {
  const uploadArr = Object.values(uploadMap);
  const all = uploadArr.length;

  if (!isUploading && _.isEmpty(uploadArr)) {
    return {
      primary: `${files.length} ${
        files.length === 1 ? 'file' : 'files'
      } ready for upload`,
    };
  }
  const finished = getUploadCompletion(uploadArr, 'finished');
  const failed = getUploadCompletion(uploadArr, 'error');

  if (!isUploading && !_.isEmpty(uploadArr)) {
    if (!failed.count) {
      return {
        primary: `All ${all} ${
          files.length === 1 ? 'file' : 'files'
        } uploaded successfully`,
      };
    }

    return {
      primary: `Upload finished. ${failed.count} out of ${all} ${
        all === 1 ? 'file' : 'files'
      } failed to upload`,
    };
  }

  return {
    primary: `Uploading ${all} ${
      all === 1 ? 'file' : 'files'
    }. Do not close browser tab!`,
    secondary: `${finished.percent}% complete`,
  };
};

const leadingZeroFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 2,
});

export const getDurationDisplay = (time: number | undefined) => {
  if (!time) {
    return '00:00:00';
  }
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60) % 60;
  const hours = Math.floor(time / 3600);
  if (hours === 0) {
    return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
      minutes,
    )}:${leadingZeroFormatter.format(seconds)}`;
  }
  return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
    minutes,
  )}:${leadingZeroFormatter.format(seconds)}`;
};

export const getFileNameWihoutExtension = (name: string) => {
  if (!name) return name;
  const divider =
    name.toLowerCase().lastIndexOf('.mp4') > -1
      ? name.toLowerCase().lastIndexOf('.mp4')
      : name.toLowerCase().lastIndexOf('.mov');
  if (divider > -1) return name.substring(0, divider);
  return name;
};

const millisecondsFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 3,
});

export const getFfmpegTimeFormat = (time: number | undefined) => {
  if (!time) {
    return '00:00:00';
  }
  const miliseconds = _.round(time - Math.floor(time), 3) * 1000;
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60) % 60;
  const hours = Math.floor(time / 3600);
  if (hours === 0) {
    return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
      minutes,
    )}:${leadingZeroFormatter.format(seconds)}${
      miliseconds > 0 ? '.' + miliseconds : ''
    }`;
  }
  return `${leadingZeroFormatter.format(hours)}:${leadingZeroFormatter.format(
    minutes,
  )}:${leadingZeroFormatter.format(seconds)}${
    miliseconds > 0 ? '.' + millisecondsFormatter.format(miliseconds) : ''
  }`;
};

export const getVideoTimeFormat = (time: number | undefined) => {
  if (!time) {
    return '0:00';
  }
  const miliseconds = _.round(time - Math.floor(time), 1) * 10;
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor(time / 60) % 60;
  const hours = Math.floor(time / 3600);
  if (hours === 0) {
    return `${minutes}:${leadingZeroFormatter.format(seconds)}.${miliseconds}`;
  }
  return `${hours}:${leadingZeroFormatter.format(
    minutes,
  )}:${leadingZeroFormatter.format(seconds)}`;
};

export const typeToLabel = (type?: string): 'Clip' | 'Video' =>
  type === 'long' ? 'Video' : 'Clip';

export const durationToLabel = (duration: number): 'Clip' | 'Video' =>
  duration > 60 ? 'Video' : 'Clip';
